import { useState } from "react";
import { motion } from "framer-motion";
import { Building, Users, HeartPulse, Send, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const TIERS = [
  { id: "corp-basic", name: "Startup Care", price: 799, unit: "/employee/yr", tag: "10-50 staff", points: ["Annual on-site health camp", "CBC, HbA1c, lipid, LFT/KFT, ECG", "Digital reports for every employee", "Dedicated coordinator"] },
  { id: "corp-growth", name: "Growth Care", price: 1499, unit: "/employee/yr", tag: "50-200 staff", points: ["Everything in Startup Care", "Thyroid, Vitamin D & B12 panels", "USG abdomen screening", "Quarterly wellness dashboard for HR"] },
  { id: "corp-exec", name: "Executive Care", price: 2999, unit: "/employee/yr", tag: "Leadership teams", points: ["70+ parameter full-body checkup", "Specialist report review call", "Priority home/office collection", "Hemozy+ membership for leadership"] },
];

export default function CorporateWellness() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Enter your company / contact name");
    if (!/^\d{10}$/.test(form.phone)) return toast.error("Enter a valid 10-digit phone number");
    if (!form.message.trim()) return toast.error("Tell us your team size and needs");
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, type: "corporate" });
      toast.success("Request sent! Our corporate team will call you within 24 hours.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="corporate" className="bg-white py-20 lg:py-28" data-testid="corporate-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="flex items-center gap-4 mb-6">
          <span className="font-mono-med text-sm text-[#D92638] border border-[#D92638]/40 rounded-full px-3.5 py-1">05</span>
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500">Corporate wellness</span>
          <span className="h-px flex-1 bg-slate-200" />
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <motion.h2 variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] leading-tight max-w-xl">
              Healthier teams, zero logistics. We bring the lab to your office.
            </motion.h2>
            <motion.p variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="mt-5 text-base text-slate-600 leading-relaxed max-w-xl">
              Annual employee health checkups, on-site camps and executive programs for Vizag companies - with digital reports, HR dashboards and specialist-reviewed accuracy.
            </motion.p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {TIERS.map((t, i) => (
                <motion.div key={t.id} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} transition={{ delay: i * 0.08 }}
                  className="bg-[#F8FAFC] rounded-2xl border border-slate-200/90 p-6 hover:-translate-y-1 hover:shadow-[0_12px_32px_-6px_rgba(15,25,44,0.12)] transition-all duration-300"
                  data-testid={`corporate-tier-${t.id}`}>
                  <span className="text-[10px] font-bold tracking-widest uppercase text-[#0D9488] bg-[#0D9488]/10 rounded-full px-3 py-1">{t.tag}</span>
                  <h3 className="mt-3.5 font-bold text-[#0B192C]">{t.name}</h3>
                  <div className="mt-1.5 flex items-baseline gap-1">
                    <span className="font-mono-med text-2xl font-bold text-[#0B192C]">₹{t.price.toLocaleString("en-IN")}</span>
                    <span className="text-[11px] text-slate-500">{t.unit}</span>
                  </div>
                  <ul className="mt-4 space-y-2">
                    {t.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed">
                        <Check className="w-3.5 h-3.5 text-[#0D9488] shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
              className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-600">
              <span className="flex items-center gap-2"><Building className="w-4 h-4 text-[#D92638]" /> On-site sample camps</span>
              <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#D92638]" /> HR wellness dashboards</span>
              <span className="flex items-center gap-2"><HeartPulse className="w-4 h-4 text-[#D92638]" /> GST invoicing & TPA support</span>
            </motion.div>
          </div>

          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="lg:col-span-5">
            <form onSubmit={submit} className="bg-[#0B192C] grain relative overflow-hidden rounded-[1.75rem] p-7 lg:p-8" data-testid="corporate-form">
              <div className="absolute inset-0 dot-grid opacity-40" />
              <div className="relative">
                <h3 className="font-bold text-white text-lg">Get a corporate quote</h3>
                <p className="text-xs text-slate-400 mt-1 mb-5">Custom pricing for 200+ employee tie-ups</p>
                <div className="space-y-3.5">
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Company / contact name *" data-testid="corporate-name-input"
                    className="w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/50 focus:border-[#14B8A6]" />
                  <div className="grid grid-cols-2 gap-3.5">
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="Phone *" inputMode="numeric" data-testid="corporate-phone-input"
                      className="rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/50 focus:border-[#14B8A6]" />
                    <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email" data-testid="corporate-email-input"
                      className="rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/50 focus:border-[#14B8A6]" />
                  </div>
                  <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="Team size, locations, what you need *" data-testid="corporate-message-input"
                    className="w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#14B8A6]/50 focus:border-[#14B8A6]" />
                  <button type="submit" disabled={sending} data-testid="corporate-submit-button"
                    className="w-full flex items-center justify-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white font-semibold py-3.5 rounded-full transition-all duration-200 active:scale-[0.98] disabled:opacity-60">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {sending ? "Sending..." : "Request corporate quote"}
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
